/**
 * MessageInput - Text input for sending messages
 */

import React, { useRef, useEffect, KeyboardEvent } from 'react';

interface MessageInputProps {
  onSend: (body: string) => void;
  value: string;
  onChange: (body: string) => void;
  isSending: boolean;
  placeholder?: string;
  autoFocus?: boolean;
  disabled?: boolean; // Disable sending when conversation is not active (users no longer friends)
  disabledMessage?: string; // Message to show when disabled
}

export const MessageInput: React.FC<MessageInputProps> = ({
  onSend,
  value,
  onChange,
  isSending,
  placeholder = 'Type a message...',
  autoFocus = false,
  disabled = false,
  disabledMessage = 'You are no longer friends with this user. Messages cannot be sent.',
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-focus when component mounts
  useEffect(() => {
    if (autoFocus && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [autoFocus]);

  // Auto-grow textarea and control scrollbar visibility
  useEffect(() => {
    if (textareaRef.current) {
      // Reset height to auto to get accurate scrollHeight
      textareaRef.current.style.height = 'auto';
      const scrollHeight = textareaRef.current.scrollHeight;
      const maxHeight = 120; // Max ~5 lines (~120px) - increased for better visibility
      const minHeight = 44; // Minimum height for comfortable typing
      const newHeight = Math.max(minHeight, Math.min(scrollHeight, maxHeight));
      textareaRef.current.style.height = `${newHeight}px`;
      
      // Only show scrollbar when content exceeds max height
      if (scrollHeight > maxHeight) {
        textareaRef.current.style.overflowY = 'auto';
      } else {
        textareaRef.current.style.overflowY = 'hidden';
      }
    }
  }, [value]);

  const handleSend = () => {
    const trimmedBody = value.trim();
    if (!trimmedBody || isSending || disabled) return;

    onSend(trimmedBody);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    // Enter without Shift = send
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
    // Shift+Enter = new line (default behavior)
  };

  const isDisabled = !value.trim() || isSending || disabled;

  return (
    <div className="flex flex-col p-4 border-t border-gray-200 bg-white flex-shrink-0">
      {disabled && (
        <div className="mb-2 px-3 py-2 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-sm text-yellow-800">{disabledMessage}</p>
        </div>
      )}
      <div className="flex items-end space-x-2">
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          rows={1}
          disabled={isSending}
          className="flex-1 resize-none rounded-lg border border-gray-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
          style={{ minHeight: '44px', maxHeight: '120px', overflowY: 'hidden', lineHeight: '1.5' }}
        />
        <button
          onClick={handleSend}
          disabled={isDisabled}
          className={`flex-shrink-0 p-2 rounded-lg transition-colors ${
            isDisabled
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-blue-500 text-white hover:bg-blue-600'
          }`}
          aria-label="Send message"
        >
        {isSending ? (
          // Loading spinner
          <svg
            className="animate-spin h-5 w-5"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
        ) : (
          // Send icon (paper plane)
          <svg
            className="h-5 w-5"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
            />
          </svg>
        )}
      </button>
      </div>
    </div>
  );
};
